// CFAST-native visual style — dark navy/black backgrounds, white text,
// dark blue title banners, A-E answer lists with "Answer:" input field.
// Matches the aesthetic of CFAST candidate guide screenshots.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LockKeyhole, Settings } from 'lucide-react';
import { trackGrowth } from '../utils/growthAnalytics';
import { useAuth } from '../contexts/AuthContext';
import { useMockTest } from '../contexts/MockTestContext';
import OnScreenKeypad, { KEYPAD_RESERVED_SPACE_PX } from '../components/OnScreenKeypad';
import { useIsMobile } from '../hooks/useIsMobile';
import { getModuleHistory, getSettings } from '../utils/storage';
import { getResultAccuracy } from '../utils/cbatScoring';
import { describeWeakness, describeWeaknessTrend } from '../utils/errorReview';
import { getPracticeConfig, getPracticeSettingsFields, hasPracticeChanges } from '../utils/practiceSettings';
import PracticeSettingsDialog, { formatPracticeDuration } from '../components/PracticeSettingsDialog';
import { WebGLFixSteps } from '../components/SceneUnavailableNotice';
import { getWebGLSupport } from '../utils/webgl';

export const CFAST_BG = '#000018';
export const CFAST_PANEL = '#000030';
export const CFAST_BANNER = '#0000B0';
export const CFAST_BUTTON = '#0000A0';
export const CFAST_BUTTON_HOVER = '#0000CC';
export const CFAST_ACCENT = '#FF3333';

export const cbtFont = { fontFamily: "'Arial', 'Helvetica', sans-serif" };
export const monoFont = { fontFamily: "'Courier New', 'Consolas', monospace" };
export const bevelOut = { borderStyle: 'outset', borderWidth: '2px', borderColor: '#4444AA' };
export const bevelIn = { borderStyle: 'inset', borderWidth: '2px', borderColor: '#000060' };

export const pad2 = (n) => String(Math.floor(n)).padStart(2, '0');
export const pad3 = (n) => String(Math.floor(n)).padStart(3, '0');
export const formatClock = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
export const formatTime = (s) => `${pad2(Math.floor(Math.max(0, s) / 60))}:${pad2(Math.floor(Math.max(0, s) % 60))}`;

export const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
export const randFloat = (min, max) => Math.random() * (max - min) + min;
export const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
export const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Simple bordered blue button
export const CFASTButton = React.forwardRef(({ children, onClick, testid, disabled, active, type = 'button', className = '', ...props }, ref) => (
  <button ref={ref} type={type} data-testid={testid} onClick={onClick} disabled={disabled}
    className={`site-button ${active ? 'site-button--primary' : ''} ${className}`}
    {...props}>
    {children}
  </button>
));
CFASTButton.displayName = 'CFASTButton';

// Blue-headed panel with dark navy content
export const CFASTPanel = ({ title, children, testId, className = '' }) => (
  <div className={`site-panel overflow-hidden ${className}`} data-testid={testId}>
    {title && <div className="site-panel__header"><span>{title}</span><span className="h-1.5 w-1.5 rounded-full bg-[#68d9ff] shadow-[0_0_8px_#68d9ff]" /></div>}
    <div className="p-4">{children}</div>
  </div>
);

// Menu list (like Airborne Numerical's left menu)
export const CFASTMenu = ({ items, active }) => (
  <div className="bg-[#000030] border border-[#4444AA]">
    <div className="bg-[#0000B0] text-white text-center py-0.5 text-xs font-bold">Menu</div>
    <div className="p-0">
      {items.map((it, i) => (
        <div key={i}
          className={`px-2 py-1 text-xs text-white border-b border-[#222266] ${active === i ? 'bg-[#0000A0]' : ''}`}>
          {it}
        </div>
      ))}
    </div>
  </div>
);

// A/B/C/D/E options list — CFAST format
export const CFASTOptions = ({ options, prefix = 'answer' }) => (
  <div className="space-y-1" data-testid={`${prefix}-list`}>
    {options.map((opt, i) => (
      <div key={i} className="flex items-baseline text-white text-sm" data-testid={`${prefix}-${i}`}>
        <span className="font-bold text-white w-6">{String.fromCharCode(65 + i)}</span>
        <span className="font-mono">{typeof opt === 'object' ? opt.label : opt}</span>
      </div>
    ))}
  </div>
);

// Answer input field — user types letter (A-E) or number, presses Enter
export const CFASTAnswerInput = ({ options, onSubmit, testid = 'answer-input', optionCount = 5 }) => {
  const [value, setValue] = useState('');
  const isMobile = useIsMobile();
  const submit = (e) => {
    e.preventDefault();
    if (!value) return;
    const letter = value.trim().toUpperCase();
    const idx = 'ABCDE'.indexOf(letter);
    if (idx >= 0 && idx < optionCount) {
      const opt = options ? options[idx] : letter;
      onSubmit(opt, idx);
      setValue('');
    } else {
      // Try numeric match against option values
      if (options) {
        const num = value.trim();
        const foundIdx = options.findIndex(o => String(typeof o === 'object' ? o.label : o).replace(/[^\d.-]/g, '') === num);
        if (foundIdx >= 0) {
          onSubmit(options[foundIdx], foundIdx);
          setValue('');
        }
      }
    }
  };
  return (
    <form onSubmit={submit} className="flex items-center gap-2">
      <label className="text-white text-sm">Answer:</label>
      <input data-testid={testid} value={value} onChange={e => setValue(e.target.value)}
        className="bg-black text-white font-mono text-lg px-2 py-0.5 w-20 border border-white uppercase focus:outline-none"
        maxLength={4} autoFocus inputMode={isMobile ? 'none' : undefined} />
      <button type="submit" className="text-white text-xs bg-[#0000A0] px-2 py-0.5 border border-[#4444AA]">ENTER</button>
      {isMobile && (
        <OnScreenKeypad
          testid={`${testid}-keypad`}
          keys={'ABCDE'.slice(0, optionCount).split('')}
          onKey={(key) => setValue((v) => (v + key).slice(0, 4))}
          onBackspace={() => setValue((v) => v.slice(0, -1))}
        />
      )}
    </form>
  );
};

// Menu screen for a module — practice/assessment + difficulty selector
// moduleName/settingsKey are both optional -- omitting them (as every
// pre-existing call site does) reproduces the old behavior exactly. Passing
// them adds a "last attempt" summary and a per-difficulty duration/item
// preview to this one shared setup screen, which every module renders
// first -- the single highest-leverage place to surface that context.
export const ModuleMenu = ({
  title,
  description,
  mode,
  setMode,
  difficulty,
  setDifficulty,
  onStart,
  onCancel,
  children,
  showDifficulty = true,
  assessmentDescription = 'Assessment uses one progressive setting and automatically advances from easy through medium to hard.',
  moduleName,
  settingsKey,
  needsWebGL = false
}) => {
  const { user, hasFullAccess, isAdmin } = useAuth();
  // A 3D module warns here, before a run, when this browser can't start
  // WebGL 2 (see utils/webgl.js).
  const webglMissing = useMemo(() => needsWebGL && !getWebGLSupport().webgl2, [needsWebGL]);
  const mockTest = useMockTest();
  const assessmentLocked = !hasFullAccess && !isAdmin;

  const lastAttempt = useMemo(() => {
    if (!moduleName) return null;
    const assessments = (getModuleHistory(moduleName) || []).filter((attempt) => attempt.mode === 'assessment');
    const scored = assessments
      .map((attempt) => ({ attempt, accuracy: getResultAccuracy(attempt) }))
      .filter((entry) => entry.accuracy !== null);
    if (!scored.length) return null;
    const sorted = [...scored].sort((a, b) => new Date(b.attempt.timestamp) - new Date(a.attempt.timestamp));
    return {
      latest: sorted[0].accuracy,
      best: Math.max(...scored.map((entry) => entry.accuracy)),
      attempts: assessments.length
    };
  }, [moduleName]);

  // Read again after the gear's settings are saved.
  const [settingsVersion, setSettingsVersion] = useState(0);
  const [practiceSettingsOpen, setPracticeSettingsOpen] = useState(false);
  const gearRef = useRef(null);
  const moduleSettings = useMemo(
    () => (settingsKey ? getSettings()?.[settingsKey] : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settingsKey, settingsVersion]
  );
  const practiceFields = settingsKey ? getPracticeSettingsFields(settingsKey) : null;
  // The gear is for practice only: assessments keep the standard settings.
  const showPracticeSettings = !mockTest.active && mode === 'practice' && Boolean(practiceFields && moduleSettings);
  const difficultyPreview = (tier) => {
    const cfg = getPracticeConfig(moduleSettings, tier);
    if (!cfg) return null;
    const parts = [];
    if (Number.isFinite(cfg.questionCount)) parts.push(`${cfg.questionCount} item${cfg.questionCount === 1 ? '' : 's'}`);
    if ((cfg.testDuration || 0) >= 30) parts.push(formatPracticeDuration(cfg.testDuration));
    return parts.length ? { text: parts.join(' · '), custom: hasPracticeChanges(moduleSettings, tier) } : null;
  };
  const closePracticeSettings = () => {
    setPracticeSettingsOpen(false);
    gearRef.current?.focus();
  };
  const canStart = mockTest.active ? mode === 'assessment' : mode && (mode === 'assessment' || difficulty);

  useEffect(() => {
    if (mockTest.active && mode !== 'assessment') setMode('assessment');
  }, [mockTest.active, mode, setMode]);

  return (
    <div className="site-page flex min-h-screen items-center justify-center" data-module-stage="menu">
      <div className="site-panel w-[760px] max-w-full overflow-hidden">
        <div className="site-panel__header">
          <span>Module configuration</span>
          {showPracticeSettings && (
            <button
              ref={gearRef}
              type="button"
              className="site-icon-button gap-1.5 px-2.5 py-1.5 text-[10px] tracking-[0.13em]"
              data-testid="practice-settings-btn"
              aria-haspopup="dialog"
              title="Practice settings"
              onClick={() => setPracticeSettingsOpen(true)}
            >
              <Settings size={14} aria-hidden="true" />
              <span>Settings</span>
            </button>
          )}
        </div>
        <div className="px-6 py-7 text-white sm:px-8">
          <h1 className="font-['IBM_Plex_Sans_Condensed'] text-3xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#8fa5bd]">{description}</p>
          <div className="mb-7 mt-4">
            {lastAttempt && (
              <div
                className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-[#263b53] bg-[#081321] px-4 py-2.5 text-xs text-[#9db1c5]"
                data-testid="module-menu-last-attempt"
              >
                <span>Your last assessment: <span className="font-mono font-semibold text-[#edf5ff]">{lastAttempt.latest.toFixed(0)}%</span></span>
                <span>Best: <span className="font-mono font-semibold text-[#67e0ae]">{lastAttempt.best.toFixed(0)}%</span></span>
                <span className="text-[#60758d]">{lastAttempt.attempts} assessment{lastAttempt.attempts === 1 ? '' : 's'} so far</span>
              </div>
            )}
          </div>
          <div className="panel-label mb-2">{mockTest.active ? 'Mock test assessment' : '01 // Select mode'}</div>
          {mockTest.active ? (
            <div className="mb-6 rounded-md border border-[#31506c] bg-[#0c2135] px-4 py-3 text-xs leading-relaxed text-[#b9cbdd]">
              Test {mockTest.currentIndex + 1} of {mockTest.totalModules}. This module will run in assessment mode and its score will be added to your mock-test report.
            </div>
          ) : <div className="mb-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {[{ v: 'practice', l: 'Practice' }, { v: 'assessment', l: 'Assessment' }].map(o => {
              const disabled = o.v === 'assessment' && assessmentLocked;
              return (
                <button
                  key={o.v}
                  data-testid={`${o.v}-mode-btn`}
                  onClick={() => !disabled && setMode(o.v)}
                  disabled={disabled}
                  className={`min-h-[54px] rounded-md border px-4 text-left text-xs font-semibold transition-colors ${
                    mode === o.v
                      ? 'border-[#68d9ff] bg-[#13314a] text-white shadow-[inset_3px_0_#68d9ff]'
                      : 'border-[#2b4159] bg-[#0a1527] text-[#aabdd0] hover:border-[#49647f] hover:bg-[#0e1d32]'
                  } ${disabled ? 'cursor-not-allowed opacity-35' : ''}`}
                >
                  <span className="block font-mono text-[9px] uppercase tracking-[0.14em] text-[#60758d]">{o.v === 'practice' ? 'Open training' : 'Recorded run'}</span>
                  <span className="mt-1 block">{o.l}</span>
                </button>
              );
            })}
          </div>}
          {!mockTest.active && assessmentLocked && (
            <div className="mb-6 rounded-md border border-[#725f35] bg-[#241c0d] px-4 py-3 text-xs leading-relaxed text-[#ecd39d]">
              {!user
                ? <><a className="font-semibold underline" href="/auth">Login</a> to access assessment mode and other features.</>
                : <>Assessment mode requires a subscription. <a className="font-semibold underline" href="/subscription">View plans</a></>}
            </div>
          )}
          {!mockTest.active && mode === 'practice' && showDifficulty && (<>
            <div className="panel-label mb-2">02 // Select difficulty</div>
            <div className="grid grid-cols-3 gap-2">
              {['easy', 'medium', 'hard'].map(d => {
                const preview = difficultyPreview(d);
                return (
                  <button key={d} data-testid={`difficulty-${d}-btn`} onClick={() => setDifficulty(d)}
                    className={`flex min-h-[42px] flex-col items-center justify-center gap-0.5 rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      difficulty === d
                        ? 'border-[#7798ff] bg-[#1a2c55] text-white'
                        : 'border-[#2b4159] bg-[#0a1527] text-[#8fa5bd] hover:border-[#49647f] hover:text-white'
                    }`}>
                    <span>{d}</span>
                    {preview && (
                      <span className="font-mono text-[9px] font-normal normal-case tracking-normal text-[#60758d]" data-testid={`difficulty-${d}-preview`}>
                        {preview.text}
                        {preview.custom && <span className="text-[#68d9ff]"> · custom</span>}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </>)}
          {!mockTest.active && mode === 'assessment' && (
            <div className="rounded-md border border-[#2b4159] bg-[#0a1527] px-4 py-3 text-xs leading-relaxed text-[#b9cbdd]">
              {assessmentDescription}
            </div>
          )}
          {children}
          {webglMissing && (
            <div className="mt-6 rounded-md border border-[#725f35] bg-[#241c0d] px-4 py-3 text-xs leading-relaxed text-[#ecd39d]" data-testid="module-menu-webgl-missing" role="alert">
              <div className="font-semibold text-[#ffe3a6]">This test needs 3D graphics (WebGL 2), and this browser can&rsquo;t start them right now, so its scene would stay blank.</div>
              <WebGLFixSteps linkColor="#ffe3a6" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-[#263a50] bg-[#060d19] px-6 py-4 sm:px-8">
          <CFASTButton testid="back-to-dashboard" onClick={mockTest.active ? mockTest.leaveMockTest : onCancel}>{mockTest.active ? 'Save & exit' : 'Cancel'}</CFASTButton>
          {canStart && <CFASTButton className="site-button--primary" testid="start-test-btn" onClick={() => { onStart(); trackGrowth('practice_start'); window.dispatchEvent(new Event('cbat-practice-start')); }}>{mockTest.active ? 'Begin test →' : 'Continue →'}</CFASTButton>}
        </div>
      </div>
      {practiceSettingsOpen && showPracticeSettings && (
        <PracticeSettingsDialog
          title={title}
          settingsKey={settingsKey}
          moduleSettings={moduleSettings}
          initialDifficulty={difficulty}
          onClose={closePracticeSettings}
          onSaved={() => {
            setSettingsVersion((version) => version + 1);
            closePracticeSettings();
          }}
        />
      )}
    </div>
  );
};

// Reads the most recent prior assessment's score for a module -- must be
// called BEFORE saveResult() persists the run that just finished, or it
// would just find itself. Shares getResultAccuracy's academyScore fallback,
// so this works for the continuous-tracking modules too.
export const getPreviousAssessmentAccuracy = (moduleName) => {
  const assessments = (getModuleHistory(moduleName) || []).filter((attempt) => attempt.mode === 'assessment');
  const scored = assessments
    .map((attempt) => ({ attempt, accuracy: getResultAccuracy(attempt) }))
    .filter((entry) => entry.accuracy !== null);
  if (!scored.length) return null;
  const sorted = [...scored].sort((a, b) => new Date(b.attempt.timestamp) - new Date(a.attempt.timestamp));
  return sorted[0].accuracy;
};

// The per-question breakdown from the account's most recent PRIOR assessment
// attempt (before the one just finished -- call this the same way as
// getPreviousAssessmentAccuracy above, before that attempt is saved). null
// for a first attempt, or for one saved before per-question capture existed.
// Lets a results screen say "still an issue" vs. "improved" on a classified
// weak spot instead of only ever restating this session's own numbers.
export const getPreviousAssessmentItems = (moduleName) => {
  const assessments = (getModuleHistory(moduleName) || []).filter((attempt) => attempt.mode === 'assessment' && Array.isArray(attempt.items));
  if (!assessments.length) return null;
  const sorted = [...assessments].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return sorted[0].items;
};

// Small shared delta line for a results screen: "+8% vs your last attempt".
// previousAccuracy must be captured before the just-finished run was saved
// (see getPreviousAssessmentAccuracy). Renders nothing for a first attempt.
export const ResultVsLastAttempt = ({ currentAccuracy, previousAccuracy }) => {
  if (previousAccuracy === null || previousAccuracy === undefined || !Number.isFinite(currentAccuracy)) return null;
  const delta = Math.round((currentAccuracy - previousAccuracy) * 10) / 10;
  const sign = delta > 0 ? '+' : '';
  const color = delta > 0 ? 'text-[#67e0ae]' : delta < 0 ? 'text-[#ff9ba4]' : 'text-[#8fa5bd]';
  return (
    <p className={`mt-2 text-xs ${color}`} data-testid="result-vs-last-attempt">
      {sign}{delta}% vs your last attempt ({previousAccuracy.toFixed(0)}%)
    </p>
  );
};

// Small shared running-tally shown next to a module's "Item X of Y" counter
// during Practice mode -- Assessment mode never renders this (per every
// module's own "no feedback until the end" description). Practice mode
// already reveals per-item correctness in most modules; this aggregates it
// so a candidate doesn't have to keep score in their head, and is the only
// in-session feedback at all for the couple of modules that don't reveal
// per-item (e.g. Perceptual Speed & Accuracy).
export const PracticeRunningScore = ({ correct, answered }) => (
  answered > 0 ? (
    <span className="ml-2 text-[#67e0ae]" data-testid="practice-running-score">{correct}/{answered} correct so far</span>
  ) : null
);

// Standard results screen
export const ModuleAnswerSummary = ({ summary = [] }) => {
  if (!summary.length) return null;
  return (
    <div className="mt-3 bg-[#000020] border border-[#4444AA]" data-testid="answer-summary">
      <div className="bg-[#0000B0] text-white text-center py-0.5 text-xs font-bold">Answer Summary</div>
      <div className="max-h-[360px] overflow-y-auto p-2 space-y-2">
        {summary.map((item, i) => (
          <div
            key={i}
            className={`border px-2 py-1 text-xs ${item.correct ? 'bg-[#002800] border-[#008000]' : 'bg-[#300000] border-[#AA4444]'}`}
            data-testid={`answer-summary-${i}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="font-bold">Q{i + 1}: {item.prompt || item.question || item.task || 'Response'}</div>
              <div className={`font-bold ${item.correct ? 'text-[#66FF66]' : 'text-[#FF7777]'}`}>
                {item.correct ? 'CORRECT' : 'INCORRECT'}
              </div>
            </div>
            {item.detail && <div className="mt-1 text-[#CCCCCC]">{item.detail}</div>}
            {item.given !== undefined && (
              <div className="mt-1 grid grid-cols-2 gap-2">
                <div>Given: <span className="font-mono" data-testid={`answer-summary-given-${i}`}>{String(item.given)}</span></div>
                <div>Correct: <span className="font-mono" data-testid={`answer-summary-correct-${i}`}>{String(item.answer ?? item.correctAnswer)}</span></div>
              </div>
            )}
            {item.explanation && <div className="mt-1 text-[#DDDDDD]">{item.explanation}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

// Shared "What to work on" callout + free-tier locked teaser, extracted
// from NumericalReasoning.js's original inline JSX (the first module to
// build this) so every module adopting targeted error review renders the
// identical panel instead of re-copying it. classification/trend come from
// errorReview.js's classifyErrors/compareWeaknessTrend; noun is the plural
// unit named in the copy (e.g. "topic question", "attribute question").
// guidance is optional per-category coaching -- { why, fix } -- for the top
// weak category specifically (not a generic module-level tip); omit it and
// the panel renders exactly as it did before this existed, so a module can
// adopt real guidance on its own schedule without anything else changing.
export const ErrorInsightsPanel = ({ classification, trend, canSeeErrorInsights, noun = 'question', guidance = null }) => {
  if (!classification.length) return null;

  if (!canSeeErrorInsights) {
    const totalCount = classification.reduce((sum, entry) => sum + entry.count, 0);
    return (
      <div className="mt-4 flex items-start gap-3 rounded-md border border-[#4a3a1e] bg-[#241c0d] p-4" data-testid="error-insights-locked">
        <LockKeyhole className="mt-0.5 shrink-0 text-[#ecd39d]" size={15} />
        <p className="text-sm text-[#ecd39d]">
          {totalCount} {noun}{totalCount === 1 ? '' : 's'} in this session point to a specific weak spot.
          {' '}<a className="font-semibold underline" href="/subscription">Unlock targeted practice and trend tracking</a> to see which one.
        </p>
      </div>
    );
  }

  const weaknessCopy = describeWeakness(classification, noun);
  const trendCopy = describeWeaknessTrend(trend);
  return (
    <div className="mt-4 rounded-md border border-[#4a3a1e] bg-[#241c0d] p-4" data-testid="error-insights">
      <div className="panel-label !text-[#ecd39d] !text-[8px]">What to work on</div>
      <p className="mt-2 text-sm text-[#ecd39d]">{weaknessCopy}</p>
      {trendCopy && (
        <p
          className={`mt-1 text-xs ${
            trend.status === 'improved' ? 'text-[#67e0ae]' : trend.status === 'worse' ? 'text-[#ff9ba4]' : 'text-[#c9b78a]'
          }`}
          data-testid="weakness-trend"
        >
          {trendCopy}
        </p>
      )}
      {guidance && (
        <div className="mt-2 space-y-1 border-t border-[#4a3a1e] pt-2 text-xs leading-relaxed text-[#c9b78a]" data-testid="error-insights-guidance">
          <p><strong className="text-[#ecd39d]">Why this trips people up:</strong> {guidance.why}</p>
          <p><strong className="text-[#ecd39d]">What to do differently:</strong> {guidance.fix}</p>
        </div>
      )}
    </div>
  );
};

export const ModuleResults = ({
  title,
  rows,
  overallScore,
  onRetry,
  onDashboard,
  summary = [],
  overallScoreLabel = 'OVERALL SCORE',
  overallScoreSuffix = '%',
  // Optional module-specific feedback (e.g. an error-classification callout)
  // rendered between the score and the Answer Summary. null for every
  // module that doesn't pass one -- this stays a no-op for them.
  insights = null,
  // Optional extra footer button (e.g. "Practise this mistake"), rendered
  // before Try Again -- only outside a mock test, matching every other
  // module's convention of hiding retry/practice actions mid-battery. null
  // for every module that doesn't pass one.
  extraAction = null
}) => {
  const mockTest = useMockTest();
  const overallScoreText = overallScoreSuffix === '%'
    ? `${Number(overallScore || 0).toFixed(1)}${overallScoreSuffix}`
    : `${overallScore ?? 0}${overallScoreSuffix}`;

  return (
    <div className="site-page flex min-h-screen items-center justify-center" data-module-stage="results">
      <div className="site-panel w-[860px] max-w-full overflow-hidden">
        <div className="site-panel__header"><span>Session report</span><span className="text-[#67e0ae]">● Saved</span></div>
        <div className="p-6 text-white sm:p-8">
          <div className="site-kicker">Run complete</div>
          <h1 className="mb-6 mt-3 font-['IBM_Plex_Sans_Condensed'] text-3xl font-semibold">{title}</h1>
          <div className="space-y-2">
          {rows.map(([l, v], i) => (
            <div key={i} className="flex items-center justify-between rounded-md border border-[#263b53] bg-[#081321] px-4 py-2.5 text-sm" data-testid={`result-${i}`}>
              <span className="text-[#9db1c5]">{l}</span><span className="font-mono text-[#edf5ff]">{v}</span>
            </div>
          ))}
          <div className="mt-3 flex items-center justify-between rounded-md border border-[#4a718a] bg-gradient-to-r from-[#12314a] to-[#122541] px-4 py-4">
            <span className="panel-label !text-[#b8cfdf]">{overallScoreLabel}</span>
            <span className="font-mono text-2xl font-semibold text-[#68d9ff]" data-testid="overall-score">{overallScoreText}</span>
          </div>
          {insights}
          <ModuleAnswerSummary summary={summary} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#263a50] bg-[#060d19] px-6 py-4 sm:px-8">
          {mockTest.active ? (
            <CFASTButton
              className="site-button--primary"
              testid="mock-test-continue"
              onClick={() => mockTest.completeModule({ title, rows, overallScore })}
            >
              Continue mock test →
            </CFASTButton>
          ) : <>
            {extraAction}
            <CFASTButton testid="return-menu-btn" onClick={onRetry}>Try Again</CFASTButton>
            <CFASTButton className="site-button--primary" testid="return-dashboard-btn" onClick={onDashboard}>Dashboard</CFASTButton>
          </>}
        </div>
      </div>
    </div>
  );
};

// CFAST-native test shell: dark navy background + top banner + content + bottom bar
export const CFASTShell = ({ title, mode, difficulty, questionNum, questionCount, remaining, children, showTimer = true, mobileKeypadOpen = false }) => (
  <div
    className="min-h-screen bg-[#000018] p-4"
    style={mobileKeypadOpen ? { ...cbtFont, paddingBottom: KEYPAD_RESERVED_SPACE_PX } : cbtFont}
  >
    <div className="max-w-[1100px] mx-auto">
      <div className="bg-[#0000B0] text-white px-3 py-1 text-sm font-bold flex justify-between items-center border border-[#4444AA]">
        <span>{title}</span>
        {showTimer && <span className="font-mono text-xs" data-testid="time-remaining">Time Left: {formatTime(remaining)}</span>}
      </div>
      <div className="bg-[#000030] border border-[#4444AA] border-t-0 p-3">
        {children}
      </div>
      <div className="bg-[#000050] border border-[#4444AA] border-t-0 px-3 py-0.5 flex justify-between text-white text-xs">
        <span>{mode === 'practice' ? 'Practice' : 'Question'}: {questionNum} of {questionCount}</span>
        <span className="uppercase">{difficulty}</span>
      </div>
    </div>
  </div>
);

// ---- Back-compat aliases ----
// Older modules import these names. They render the same CFAST dark-navy visuals.
export const CBTReasonShell = ({ title, testTag, practice, remaining, questionNum, questionCount, children, mobileKeypadOpen }) => (
  <CFASTShell title={title} mode={practice ? 'practice' : 'assessment'} difficulty=""
    questionNum={questionNum} questionCount={questionCount} remaining={remaining} mobileKeypadOpen={mobileKeypadOpen}>
    {children}
  </CFASTShell>
);

export const CBTPanel = ({ title, children, testId }) => (
  <div className="bg-[#000030] border border-[#4444AA]" data-testid={testId}>
    {title && <div className="bg-[#0000B0] text-white text-center py-0.5 text-xs font-bold border-b border-[#4444AA]">{title}</div>}
    <div className="p-2">{children}</div>
  </div>
);

export const CBTSubPanel = ({ title, children }) => (
  <div className="bg-[#000030] border border-[#4444AA]">
    {title && <div className="bg-[#000080] text-white text-center py-0.5 text-[11px] font-bold border-b border-[#4444AA]">{title}</div>}
    <div className="p-2">{children}</div>
  </div>
);

export const CBTButton = ({ children, onClick, testid, disabled, wide }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    className={`text-white text-xs font-bold py-1 ${wide ? 'px-6' : 'px-3'} bg-[#0000A0] border-2 border-[#4444AA] hover:bg-[#0000CC] disabled:opacity-40`}>
    {children}
  </button>
);

export const CBTGreenButton = ({ children, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className="bg-[#008000] text-white px-6 py-1 text-xs font-bold border-2 border-[#00B000] hover:bg-[#00A000]">
    {children}
  </button>
);

export const LcdDigit = ({ ch, testId, small }) => (
  <div className={`${small ? 'w-5 h-5' : 'w-6 h-6'} bg-black text-white flex items-center justify-center font-mono ${small ? 'text-xs' : 'text-sm'} font-bold border border-[#4444AA]`}
    data-testid={testId}>{ch}</div>
);

// Clickable A-E answer choices (dark theme). Kept as-is for backwards compat.
export const AnswerChoices = ({ options, onPick, prefix = 'answer' }) => (
  <div className="space-y-1">
    {options.map((opt, i) => (
      <button key={i} data-testid={`${prefix}-${i}`} onClick={() => onPick(opt, i)}
        className="w-full text-left px-3 py-1.5 bg-[#000050] text-white text-sm font-mono border border-[#4444AA] hover:bg-[#0000A0]">
        <span className="font-bold mr-3">{String.fromCharCode(65 + i)}</span>
        <span>{typeof opt === 'object' ? opt.label : opt}{typeof opt === 'object' && opt.suffix ? ` ${opt.suffix}` : ''}</span>
      </button>
    ))}
  </div>
);

export const CBTTestShell = ({ title, warnings, clockDisplay, mode, difficulty, elapsed, remaining, children, mobileKeypadOpen }) => (
  <CFASTComplexShell title={title} warnings={warnings} clockDisplay={clockDisplay} mode={mode}
    difficulty={difficulty} elapsed={elapsed} remaining={remaining} mobileKeypadOpen={mobileKeypadOpen}>{children}</CFASTComplexShell>
);

export const CBTWindow = ({ title, children, footer, wide }) => (
  <div className="min-h-screen bg-[#000018] flex items-center justify-center p-6" style={cbtFont}>
    <div className={wide ? 'w-[900px]' : 'w-[720px]'} style={{ border: '1px solid #4444AA' }}>
      <div className="bg-[#0000B0] text-white text-center py-1 text-sm font-bold">{title}</div>
      <div className="bg-[#000030] text-white">{children}</div>
      {footer && <div className="bg-[#000030] p-3 border-t border-[#4444AA] flex justify-end gap-2">{footer}</div>}
    </div>
  </div>
);

// Live test shell with warnings panel + clock (for complex sim tests like Cognitive Updating)
export const CFASTComplexShell = ({ title, warnings, clockDisplay, mode, difficulty, elapsed, remaining, children, mobileKeypadOpen = false }) => (
  <div
    className="min-h-screen bg-[#000018] p-3"
    style={mobileKeypadOpen ? { ...cbtFont, paddingBottom: KEYPAD_RESERVED_SPACE_PX } : cbtFont}
  >
    <div className="max-w-[1100px] mx-auto">
      <div className="bg-[#0000B0] text-white text-center py-1 text-sm font-bold border border-[#4444AA]">{title}</div>
      <div className="bg-[#000030] border border-[#4444AA] border-t-0 p-2">
        <div className="flex gap-2 mb-2">
          <div className="flex-1 bg-black border border-[#4444AA]">
            <div className="bg-[#0000B0] text-white text-center py-0.5 text-xs font-bold border-b border-[#4444AA]">Warning Panel</div>
            <div className="h-14 px-2 py-1 overflow-hidden" data-testid="warning-list">
              <div className="grid grid-cols-3 gap-x-4 gap-y-0.5 text-[11px]">
                {(warnings || []).map((w, i) => (
                  <div key={i} className={w.red ? 'text-[#FF3333]' : 'text-[#FFCC00]'}>{w.text}</div>
                ))}
              </div>
            </div>
          </div>
          <div className="w-32 bg-black border border-[#4444AA]">
            <div className="bg-[#0000B0] text-white text-center py-0.5 text-xs font-bold">Clock</div>
            <div className="h-14 flex items-center justify-center">
              <span className="text-white font-mono text-lg" data-testid="clock-display">{clockDisplay}</span>
            </div>
          </div>
        </div>
        {children}
        <div className="mt-2 bg-[#000050] border border-[#4444AA] text-white text-[11px] px-2 py-0.5 flex justify-between">
          <span>{mode?.toUpperCase()} / {difficulty?.toUpperCase()}</span>
          <span>Elapsed: {formatTime(elapsed)}</span>
          <span data-testid="time-remaining">Time Left: {formatTime(remaining)}</span>
        </div>
      </div>
    </div>
  </div>
);
